import { ApiResponse, CustomPromise, FetchConfig, MockDataForRequestManager, RequestManager } from "./RequestManager.class";
import { MethodNotImplementedError } from "./api.types";
import { MockDatabaseCRUD } from "./mocks/MockDatabaseCRUD";
import { TableQuery } from "./mocks/database/MockDatabase";
import { APIQuery } from "../utils/sharedTypes";
import { toSnakeCase } from "../utils/stringMethods";
import { sortConfigToQueryParam } from "../utils/sortUtils";
import { INITIAL_MOCK_STATUSES } from "./initialMockStatus";

export type ApiServiceCollectionProps = {
    mocked: boolean;
    mockCRUD: MockDatabaseCRUD | null;
    requestManager: RequestManager;
};

export class ApiServiceCollection {

    constructor({
        mocked,
        mockCRUD,
        requestManager
    }: ApiServiceCollectionProps) {
        this.mocked = mocked;
        this.mockCRUD = mockCRUD;
        this.requestManager = requestManager;
        this._nextMockStatus = INITIAL_MOCK_STATUSES;
    }

    mocked: boolean = false;
    mockCRUD: MockDatabaseCRUD | null;
    requestManager: RequestManager;
    private _nextMockStatus: Record<string, (number | undefined)[]> = {};

    protected transformMockQuery = (tableName: string, query: TableQuery) => {
        // To be implemented in subclass
        return query;
    }

    protected getMockData = ({ 
        tableName, 
        queryParams, 
        sort,
        skip,
        limit,
        paginated, 
        singular, 
        getResponseStatus
    }: {
        tableName: string;
        queryParams: URLSearchParams | TableQuery | Record<string, any>;
        sort?: string,
        skip?: number;
        limit?: number;
        paginated?: boolean;
        singular?: boolean;
        getResponseStatus?: (data: any) => MockDataForRequestManager
    }) => {
        if (!this.mocked) {
            return {
                data: null,
                status: 500,
                error: {
                    message: 'Unexpected call to get mock data',
                    code: 'UNEXPECTED_ERROR',
                }
            }
        }
        if (!getResponseStatus) {
            getResponseStatus = (data: any) => {
                const status = singular && !data ? 404 : 200;
                return {
                    data,
                    status,
                    error: status < 200 || status >= 300 ? {
                        message: status === 404 ? 'Not found' : 'An unexpected eerror has occurred',
                        code: status === 404 ? 'NOT_FOUND' : 'UNEXPECTED_ERROR',
                    } : undefined
                }
            }
        }
        const db = this.mockCRUD!.db();
        const table = db.getTableByName(tableName);
        let query: TableQuery | null = null;
        let params: Record<string, any> = {};
        if (!Array.isArray(queryParams)) {
            params = queryParams instanceof URLSearchParams ? Object.fromEntries(queryParams) : (queryParams || {});
            const { skip, limit, sort, show, ...filter } = params;
            if (show) {
                filter['status'] = show;
            }
            query = Object.keys(filter).length ? this.transformMockQuery(tableName, db.queryParamsToTableQuery(filter)) : null;
        } else {
            query = queryParams;
        }
        let { skip: pSkip, limit: pLimit, sort: pSort } = params;
        if (typeof pSkip === 'string') {
            pSkip = parseInt(pSkip);
        }
        if (typeof pLimit === 'string') {
            pLimit = parseInt(pLimit);
        }
        let records: any = query ? table.query(query) : table.all();
        if (singular && records.length !== 1) {
            return getResponseStatus(null);
        }
        records = singular ? table.serialize(records[0], this.mockCRUD!.db()) : table.serializeList(records, this.mockCRUD!.db());
        if ((sort || pSort) && Array.isArray(records)) {
            records = db.sortRecords(records, (sort || pSort).split(','));
        }
        let respData = records;
        if (Array.isArray(records) && (paginated || (skip || pSkip) || (limit || pLimit))) {
            respData = db.buildPaginatedResponse(records, (skip || pSkip), (limit || pLimit));
        }
        return getResponseStatus(respData);
    }

    protected getMockDataSingular = ({ tableName, queryParams, getResponseStatus }: {
        tableName: string;
        queryParams: URLSearchParams | Record<string, any>;
        paginated?: boolean;
        singular?: boolean;
        getResponseStatus?: (data: any) => {
            data: any;
            status: number;
            error?: {
                message: string;
                code?: string;
            }
        };
    }) => this.getMockData({
        tableName,
        queryParams,
        paginated: false,
        singular: true,
        getResponseStatus,
    });

    endpointParamsToQueryParams = ({ skip = 0, limit = 10, query }: { skip?: number; limit?: number, query?: APIQuery }) => {
        const queryParams = new URLSearchParams({
            'skip': skip.toString(),
            'limit': limit.toString(),
            ...Object.entries((query?.filter || {})).reduce((acc, [key, value]) => {
                acc[toSnakeCase(key)] = String(value);
                return acc;
            }, {} as Record<string, string>)
        });
        if (query?.status) {
            queryParams.append('show', query.status)
        }
        const sort = sortConfigToQueryParam(query?.sort || null);
        if (sort) {
            queryParams.append('sort', sort);
        }
        return queryParams;
    }

    standardAuthRequest<T = any>(input: RequestInfo | URL, init?: RequestInit, config?: FetchConfig<T>): CustomPromise<ApiResponse<T>, ApiResponse<T>> {
        return this.requestManager.standardAuthRequest(input, init, config);
    }

    unauthorizedRequest<T = any>(input: RequestInfo | URL, init?: RequestInit, config?: FetchConfig<T>): CustomPromise<ApiResponse<T>, ApiResponse<T>> {
        return this.requestManager.unauthorizedRequest(input, init, config);
    }

    populateMockedData = (): void => {
        throw new MethodNotImplementedError();
    }

    getJSONHeaders(): Record<string, string> {
        return {
            Accept: "application/json",
            "Content-Type": "application/json",
        };
    }

    setNextMockStatus = (endpoint: string, status: number | undefined) => {
        if (!this._nextMockStatus[endpoint]) {
            this._nextMockStatus[endpoint] = [];
        }
        this._nextMockStatus[endpoint].push(status);
    }

    mockedStatusShouldProceed = (endpoint: string, status: number) => {
        const nextStatus = (this._nextMockStatus[endpoint] || []).shift();
        if (status < 200 || status >= 300 || (
            nextStatus && (
                nextStatus < 200
                || nextStatus >= 300
            ))
        ) {
            return false;
        }
        return true;
    }

    getNextMockStatus(endpoint: string) {
        return (this._nextMockStatus[endpoint] || []).shift();
    }
}